import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Activity, Cpu, HardDrive, MemoryStick, RefreshCw, CheckCircle2, AlertTriangle, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STATUS_COLOR = {
  normal:  { bg: 'rgba(34,197,94,0.10)', border: 'rgba(34,197,94,0.40)', fg: '#16A34A', icon: CheckCircle2 },
  warning: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.40)', fg: '#D97706', icon: AlertTriangle },
  lemot:   { bg: 'rgba(220,38,38,0.10)', border: 'rgba(220,38,38,0.40)', fg: '#DC2626', icon: Zap },
};

const BAR_COLOR = (pct) => pct >= 85 ? '#DC2626' : pct >= 65 ? '#F59E0B' : '#16A34A';

function fmtUptime(sec) {
  if (sec == null) return '-';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}h ${h}j ${m}m`;
  if (h > 0) return `${h}j ${m}m`;
  return `${m}m ${sec % 60}d`;
}

function Meter({ label, Icon, percent, sub, testId }) {
  const color = BAR_COLOR(percent);
  return (
    <div className="rounded-lg p-3 glass-card" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-[11px] font-semibold t-text uppercase tracking-wide">{label}</span>
        </div>
        <span className="text-lg font-bold t-text tabular-nums">{percent}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-[var(--bg-input)] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, percent)}%`, background: color }} />
      </div>
      <div className="text-[10px] t-text-muted mt-1.5">{sub}</div>
    </div>
  );
}

export default function AdminSystemHealth({ API, token, lang }) {
  const isID = lang !== 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/system/health`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 10000); // refresh every 10s
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const statusKey = data?.status || 'normal';
  const S = STATUS_COLOR[statusKey] || STATUS_COLOR.normal;
  const StatusIcon = S.icon;

  return (
    <div className="glass-card rounded-xl p-4 mb-4" data-testid="admin-system-health">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#D98A94]" />
          <h3 className="text-sm font-bold t-text">
            {isID ? 'Kesehatan Server' : 'Server Health'}
          </h3>
          {data && (
            <span
              className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: S.bg, border: `1px solid ${S.border}`, color: S.fg }}
              data-testid="admin-system-health-status"
            >
              <StatusIcon className="w-3 h-3" />
              {isID
                ? (statusKey === 'lemot' ? 'Lemot' : statusKey === 'warning' ? 'Sibuk' : 'Normal')
                : (data.status_label || statusKey)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] t-text-muted cursor-pointer" data-testid="admin-system-health-auto">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="rounded" />
            {isID ? 'Auto (10s)' : 'Auto (10s)'}
          </label>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="admin-system-health-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {isID ? 'Refresh' : 'Refresh'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-500 p-2 rounded bg-red-500/10 border border-red-500/25 mb-3">
          {error}
        </div>
      )}

      {data ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Meter
              label={isID ? 'CPU' : 'CPU'}
              Icon={Cpu}
              percent={data.cpu.percent}
              sub={`${data.cpu.count_logical} vCPU · load ${data.cpu.load_avg_1m}`}
              testId="admin-system-cpu"
            />
            <Meter
              label={isID ? 'Memori (RAM)' : 'Memory (RAM)'}
              Icon={MemoryStick}
              percent={data.ram.percent}
              sub={`${(data.ram.used_mb / 1024).toFixed(1)} / ${(data.ram.total_mb / 1024).toFixed(1)} GB`}
              testId="admin-system-ram"
            />
            <Meter
              label={isID ? 'Disk' : 'Disk'}
              Icon={HardDrive}
              percent={data.disk.percent}
              sub={`${data.disk.used_gb} / ${data.disk.total_gb} GB · ${data.disk.free_gb} GB free`}
              testId="admin-system-disk"
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-[10px] t-text-muted">
            <span>{isID ? 'Uptime backend' : 'Backend uptime'}: <b className="t-text">{fmtUptime(data.process_uptime_seconds)}</b></span>
            <span>{isID ? 'Diperbarui' : 'Updated'}: {new Date(data.checked_at).toLocaleTimeString()}</span>
          </div>
        </>
      ) : (
        <div className="text-xs t-text-muted py-4 text-center">
          {loading ? (isID ? 'Memuat...' : 'Loading...') : (isID ? 'Data belum tersedia' : 'No data')}
        </div>
      )}
    </div>
  );
}
